/* eslint-disable no-debugger */
import urls from '../urls';
import { CheckoutStages, GridPages } from '../constants/AppConstants';
import { GlobalModals } from '../constants/AppConstants';
import ModalActions from '../actions/ModalActions';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import t from '../lib/I18n';
import ReservationStore from '../stores/ReservationStore';

/**
 * A stage object has methods for determining the next and previous stages, and the action
 * to perform on transitioning.
 * each method will be passed the current stage and the current state
 */
export function Stage (params) {
    const defaultStage = {
        /**
         * Should return an action creator that returns a promise
         * The action creator will be passed the current state, and should
         * return any changed state
         */
        nextAction: () => null,

        /**
         * Do we need to wait for the action before continuing
         */
        waitForAction: true,

        /**
         * Should return the next stage
         */
        next: () => { throw new Error('No next stage defined'); },

        /**
         * Should return the previous stage
         */
        previous: () => { throw new Error('No previous stage defined'); },

        getParams: () => null,

        getQuery: () => null
    };
    return Object.assign(defaultStage, params);
}



export class Flow {

    constructor (stages) {
        this.stages = stages;
    }

    nextAction(current, state) {
        const action = this.stages[current].nextAction(current, state);
        return action;
    }

    nextStage(current, state) {
        return this.stages[current].next(current, state);
    }

    previousStage(current, state) {
        return this.stages[current].previous(current, state);
    }

    canContinue(current, state) {
        return this.nextStage(current, state);
    }

    mustWait(current) {
        return this.stages[current].waitForAction;
    }

    makeTransition(router, route, state, replace = false) {
        return (newState = {}) => {
            const mergedState = Object.assign({}, state, newState);

            const stage = this.stages[route];

            if (stage) {
                const params = stage.getParams(mergedState);
                const query = stage.getQuery(mergedState);
                const pathname = urls.reverse(route, params);
                if (replace) {
                    return router.replace(urls.location(route, params, query));
                }
                return router.push({ pathname, query });
            }
            return null;
        };
    }

    moveToNext(current, state, router, replace = false) {
        const action = this.nextAction(current, state);
        const next = this.nextStage(current, state);

        const handlePassengerAssistBooking = (onContinue) => {
            if (current === GridPages.SERVICES && next === CheckoutStages.DELIVERY) {

                ModalActions.open(GlobalModals.BOOK_PASSENGER_ASSIST, {
                    hideClose: true,
                    disableClose: true,
                    onClose: () => ModalActions.close(GlobalModals.BOOK_PASSENGER_ASSIST),
                    onContinue,
                    titleText: t('account.passenger-assist.book-passenger-assist.title'),
                    router,
                });
            } else {
                onContinue();
            }
        };

        const handleBikeReservations = (onContinue) => {
            if (current === GridPages.SERVICES && next === CheckoutStages.DELIVERY) {

                ModalActions.open(GlobalModals.RESERVATIONS, {
                    hideClose: true,
                    disableClose: true,
                    onClose: () => ModalActions.close(GlobalModals.RESERVATIONS),
                    onContinue,
                    titleText: 'Seat & Bike reservations',
                    router,
                });
            } else {
                onContinue();
            }
        };

        const proceedToNext = () => {
            const transition = this.makeTransition(router, next, state, replace);

            if (action) {
                let p = action(state);

                if (this.mustWait(current)) {
                    return p.then((result) => {
                        if (result) {
                            ModalActions.close(GlobalModals.BOOK_PASSENGER_ASSIST);
                            return transition.call(...arguments);
                        }
                        console.error('Transition aborted as action returned false');
                        return null;
                    });
                }
            }

            ModalActions.close(GlobalModals.BOOK_PASSENGER_ASSIST);
            return Promise.resolve(transition());
        };

        if (FeatureFlagStore.get('enablePassengerAssist')) {
            return handlePassengerAssistBooking(proceedToNext);
        }

        if (FeatureFlagStore.get('enablePurchaseFlowReservations') && ReservationStore.get('reservationWidgetToggled')) {
            return handleBikeReservations(proceedToNext);
        }

        return Promise.resolve(proceedToNext());
    }

    async moveToPrevious(current, state, router) {
        const previous = this.previousStage(current, state);
        const transition = this.makeTransition(router, previous, state);

        return transition(state);
    }

}
